import React, { Suspense, useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CycleCompleted,
  GetAssessmentCycleById,
  GetNormalizationList,
  HRNormalization,
  ratingLabelsList,
  ReleaseAppraisalResult,
  ReleaseCycleResults,
} from "./PMSService";
import { GetMasterTypeData } from "../../../Services/Global-Services/GlobalServices";
import { TostMsg } from "../../../App";
import ERROR_LIST from "../../../Services/Global-Services/Hadle_Errors";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ComponentLoader from "../../../Shared/Sidebar/ComponentLoader";

export const AssessmentNormalization = () => {
  const { guid } = useParams();

  const [employees, setEmployees] = useState([]);

  const [search, setSearch] = useState("");
  const [department, setDepartment] = useState("");
  const [labelFilter, setLabelFilter] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [releasingAppraisalId, setReleasingAppraisalId] = useState(null);
  const [releasingCycle, setReleasingCycle] = useState(false);
  const [closeCyclePopup, setCloseCyclePopup] = useState(false);
  const history = useHistory();

  const toster = useContext(TostMsg);
  const [summary, setSummary] = useState({
    stellar: 0,
    vital: 0,
    adequate: 0,
    inadequate: 0,
    pending: 0,
  });

  const [cycleDetails, setCycleDetails] = useState({});
  const [ratingLabel, setRatingLabel] = useState([]);

  useEffect(() => {
    getCycleDetails();
    // getDepartments();
    getRatingLabel();
  }, [guid]);

  const getCycleDetails = async () => {
    setLoading(true);

    const body = {
      Guid: guid,
    };

    try {
      const response = await GetAssessmentCycleById(body);

      const data = response?.Data || response?.data || {};

      const cycle =
        data?.assessmentCycle || data?.assessmentCycleDetails || data;

      setCycleDetails(cycle);
    } catch (error) {
      console.error("Get Assessment Cycle Details Error:", error);
    } finally {
      setLoading(false);
    }
  };

  /*
   * =========================================================
   * GET NORMALIZATION DATA
   * =========================================================
   */
  const getNormalizationData = async () => {
    if (!cycleDetails) {
      console.error("cycleId is missing");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const params = {
        cycleId: cycleDetails?.Id,
      };

      if (search?.trim()) {
        params.Search = search.trim();
      }

      if (department) {
        params.Department = department;
      }

      if (labelFilter) {
        params.LabelFilter = labelFilter;
      }

      const response = await GetNormalizationList(params);

      const data = response?.Data;

      if (!data) {
        setEmployees([]);
        setSummary({
          stellar: 0,
          vital: 0,
          adequate: 0,
          inadequate: 0,
          pending: 0,
        });

        return;
      }

      /*
       * Set summary cards from API
       */
      setSummary({
        stellar: data?.StellarCount ?? 0,
        vital: data?.VitalCount ?? 0,
        adequate: data?.AdequateCount ?? 0,
        inadequate: data?.InadequateCount ?? 0,
        pending: data?.PendingCount ?? 0,
      });

      /*
       * Set employee rows
       */
      setEmployees(Array.isArray(data?.Rows) ? data.Rows : []);
    } catch (err) {
      console.error("Error fetching Assessment Normalization:", err);

      setEmployees([]);

      setSummary({
        stellar: 0,
        vital: 0,
        adequate: 0,
        inadequate: 0,
        pending: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!cycleDetails?.Id) return;

    const timer = setTimeout(() => {
      getNormalizationData();
    }, 500);

    return () => clearTimeout(timer);
  }, [search, department, labelFilter, cycleDetails?.Id]);

  /*
   * =========================================================
   * GET INITIALS
   * =========================================================
   */
  const getInitials = (name) => {
    if (!name) return "--";

    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((item) => item.charAt(0).toUpperCase())
      .join("");
  };

  /*
   * =========================================================
   * SCORE COLOR
   * =========================================================
   */
  const getScoreStyle = (score) => {
    const numericScore = Number(score);

    if (numericScore >= 4.5) {
      return {
        color: "#744210",
      };
    }

    if (numericScore >= 3.5) {
      return {
        color: "#276749",
      };
    }

    if (numericScore >= 2.5) {
      return {
        color: "#2c5282",
      };
    }

    if (numericScore > 0) {
      return {
        color: "#9b2c2c",
      };
    }

    return {};
  };

  /*
   * =========================================================
   * AVATAR COLOR
   * =========================================================
   */
  const getAvatarColor = (score) => {
    const numericScore = Number(score);

    if (numericScore >= 4.5) {
      return "#744210";
    }

    if (numericScore >= 3.5) {
      return "#276749";
    }

    if (numericScore >= 2.5) {
      return "#d97706";
    }

    if (numericScore > 0) {
      return "#9b2c2c";
    }

    return "#8492a6";
  };

  /*
   * =========================================================
   * RATING LABEL CLASS
   * =========================================================
   */
  const getRatingClass = (label) => {
    switch (label?.toLowerCase()) {
      case "stellar":
        return "stellar";

      case "vital":
        return "vital";

      case "adequate":
        return "adequate";

      case "inadequate":
        return "inadequate";

      default:
        return "";
    }
  };

  /*
   * =========================================================
   * CHANGE RATING LABEL
   * =========================================================
   *
   * Currently updates UI state.
   *
   * When backend save API is available, call it here.
   */
  const handleRatingChange = async (appraisalId, labelId, labelName) => {
    const employee = employees.find(
      (item) => String(item.AppraisalId) === String(appraisalId),
    );

    if (!employee) return;

    const updatedEmployee = {
      ...employee,
      FinalRatingLabelId: labelId,
      FinalRatingLabelName: labelName,
    };

    // Update UI immediately
    setEmployees((previous) =>
      previous.map((item) =>
        String(item.AppraisalId) === String(appraisalId)
          ? updatedEmployee
          : item,
      ),
    );

    // Hit normalization API
    await normalizeAppraisal(updatedEmployee);
    await getNormalizationData();
  };

  /*
   * =========================================================
   * PROMOTION CHANGE
   * =========================================================
   */
  const handlePromotionChange = async (appraisalId, checked) => {
    const employee = employees.find(
      (item) => String(item.AppraisalId) === String(appraisalId),
    );

    if (!employee) return;

    const updatedEmployee = {
      ...employee,
      IsPromotionRecommended: checked,
    };

    // Update UI
    setEmployees((previous) =>
      previous.map((item) =>
        String(item.AppraisalId) === String(appraisalId)
          ? updatedEmployee
          : item,
      ),
    );

    // Hit normalization API
    await normalizeAppraisal(updatedEmployee);
    await getNormalizationData();
  };

  /*
   * =========================================================
   * PIP CHANGE
   * =========================================================
   */
  const handlePIPChange = async (appraisalId, checked) => {
    const employee = employees.find(
      (item) => String(item.AppraisalId) === String(appraisalId),
    );

    if (!employee) return;

    const updatedEmployee = {
      ...employee,
      IsPIPRecommended: checked,
    };

    // Update UI
    setEmployees((previous) =>
      previous.map((item) =>
        String(item.AppraisalId) === String(appraisalId)
          ? updatedEmployee
          : item,
      ),
    );

    // Hit normalization API
    await normalizeAppraisal(updatedEmployee);
    await getNormalizationData();
  };

  /*
   * =========================================================
   * HR COMMENT CHANGE
   * =========================================================
   */
  const handleHRCommentChange = (appraisalId, value) => {
    setEmployees((previous) =>
      previous.map((item) =>
        String(item.AppraisalId) === String(appraisalId)
          ? {
              ...item,
              HrComment: value,
            }
          : item,
      ),
    );
  };

  const handleHRCommentBlur = async (appraisalId) => {
    const employee = employees.find(
      (item) => String(item.AppraisalId) === String(appraisalId),
    );

    if (!employee) return;

    try {
      const success = await normalizeAppraisal(employee);

      if (success) {
        await getNormalizationData();
      }
    } catch (error) {
      console.error("Failed to save HR comment:", error);
    }
  };

  /*
   * =========================================================
   * DEPARTMENTS
   * =========================================================
   */

  const getRatingLabel = async () => {
    try {
      const response = await ratingLabelsList();

      if (response?.StatusCode === 2000) {
        setRatingLabel(response?.Data);
      }
    } catch (error) {}
  };

  const normalizeAppraisal = async (appraisal) => {
    if (!appraisal?.AppraisalId) {
      console.error("AppraisalId is missing");
      return false;
    }

    setLoading(true);
    const body = {
      FinalRatingLabelId: appraisal?.FinalRatingLabelId || 0,
      IsPromotionRecommended: Boolean(appraisal?.IsPromotionRecommended),
      IsPIPRecommended: Boolean(appraisal?.IsPIPRecommended),
      HrComment: appraisal?.HrComment || "",
    };

    try {
      await HRNormalization(appraisal.AppraisalId, body);
setLoading(false);
      
    } catch (error) {
      console.error("Failed to normalize appraisal:", error);

      toster?.current?.error(
        error?.response?.data?.Message ||
          error?.response?.data?.message ||
          "Failed to update appraisal",
      );

     setLoading(false);
    }
  };

  const handleExport = () => {
    if (!employees || employees.length === 0) {
      alert("No employee data available to export.");
      return;
    }

    const headers = [
      "Employee",
      "Employee ID",
      "Department",
      "Designation",
      "Computed Score",
      "Manager Score",
      "Final Rating Label",
      "Promotion Recommended",
      "PIP Recommended",
      "HR Comment",
      "Letter Generated",
    ];

    const rows = employees.map((employee) => [
      employee?.EmployeeName || "",
      employee?.EmpId || "",
      employee?.Department || "",
      employee?.Designation || "",
      employee?.ComputedScore ?? 0,
      employee?.ManagerFinalScore ?? 0,
      employee?.FinalRatingLabelName || "",
      employee?.IsPromotionRecommended ? "Yes" : "No",
      employee?.IsPIPRecommended ? "Yes" : "No",
      employee?.HrComment || "",
      employee?.LetterGenerated ? "Yes" : "No",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => {
            const stringValue = String(value ?? "");
            return `"${stringValue.replace(/"/g, '""')}"`;
          })
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;

    const fileName = `HR_Normalization_Export_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    link.setAttribute("download", fileName);

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ---------------------------------------------------------
  // RELEASE SINGLE EMPLOYEE RESULT
  // ---------------------------------------------------------

  const publishEmployee = async (appraisalId) => {
    if (!appraisalId) {
      toster?.current?.error("Appraisal ID is missing.");
      return;
    }
setLoading(true);
    try {
      setReleasingAppraisalId(appraisalId);

      const params = {};

      const response = await ReleaseAppraisalResult(
        Number(appraisalId),
        params,
      );

   
if(response?.StatusCode === 2000){
      toster?.current?.success(
        response?.Message ||
          response?.message ||
          "Appraisal result released successfully.",
      );

      // Refresh normalization list
      await getNormalizationData();
    }
    setLoading(false);
    } catch (error) {
      console.error("Failed to release appraisal:", error);

      toster?.current?.error(
        error?.response?.data?.Message ||
          error?.response?.data?.message ||
          "Failed to release appraisal result.",
      );
    } finally {
      setReleasingAppraisalId(null);
      setLoading(false);
    }
  };

  // ---------------------------------------------------------
  // RELEASE ALL EMPLOYEES FOR CYCLE
  // ---------------------------------------------------------

  const releaseCycle = async () => {
    const cycleId = cycleDetails?.Id;

    if (!cycleId) {
      toster?.current?.error("Cycle ID is missing.");
      return;
    }

    setLoading(true);
    try {
      setReleasingCycle(true);

      const params = {};

      const response = await ReleaseCycleResults(Number(cycleId), params);

      console.log("Cycle release response:", response);

      if(response?.StatusCode === 2000){
      toster?.current?.success(
        response?.Message ||
          response?.message ||
          "All appraisal results released successfully.",
      );

      // Refresh normalization data
      await getNormalizationData();
    }
    setLoading(false);
    } catch (error) {
      console.error("Failed to release cycle:", error);

      toster?.current?.error(
        error?.response?.data?.Message ||
          error?.response?.data?.message ||
          "Failed to release all appraisal results.",
      );
    } finally {
      setReleasingCycle(false);
      setLoading(false);
    }
  };

  const confirmMarkAsComplete = async () => {
    const params = {
      id: Number(cycleDetails?.Id),
    };

    setLoading(true);
    try {
      const response = await CycleCompleted(params);

      if (response?.StatusCode === 2000) {
        setCloseCyclePopup(false);
        history.push(`/Assessment/Cycles`);
        toster.current.success("Cycle marked as complete successfully");
      } else {
        toster.current.error("Failed to mark cycle as complete");
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const viewAppraisalDetails = (guid) => {
    history.push(`/Assessment/Employee/Dashboard/View/${guid}`);
  };

  /*
   * =========================================================
   * RENDER
   * =========================================================
   */
  return (
    <div className="container-fluid">
      <div className="row g-3 assessment-module">
        <div className="og-main">
          {/* =================================================
              PAGE HEADER
          ================================================== */}

          <div className="og-page-header Page-head">
            <div className="leftside-head">
              <div className="og-breadcrumb">
                <a
                  href='/Assessment/Cycles'
                  style={{
                    color: "#EC4119",
                    textDecoration: "none",
                  }}
                >
                  {cycleDetails?.Title}
                </a>{" "}
                › Normalization
              </div>

              <div className="page-title">HR Normalization</div>

              <div className="og-page-sub">
                Assign final rating labels, override recommendations, and close
                appraisals.
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 7,
              }}
            >
              <button
                type="button"
                className="btn btn-outline btn-sm btn-gray"
                onClick={handleExport}
              >
                <i className="fa fa-download"></i> Export
              </button>

              {normalizeAppraisal?.IsReadyToReleaseAll && (
                <button
                  type="button"
                  className="btn-og btn-og-dark btn-sm"
                  onClick={releaseCycle}
                  disabled={releasingCycle}
                >
                  <i className="fa fa-lock"></i> Publish All
                </button>
              )}

              <button
                type="button"
                className="btn-og btn-og-dark btn-sm"
                onClick={() => setCloseCyclePopup(true)}
              >
                <i className="fa fa-lock"></i> Close Cycle
              </button>
            </div>
          </div>

          {/* =================================================
              SUMMARY CARDS
          ================================================== */}

          <div
            className="og-kpi-grid"
            style={{
              gridTemplateColumns: "repeat(4,1fr)",
              marginBottom: 9,
            }}
          >
            <div className="og-kpi">
              <div
                className="og-kpi-label"
                style={{
                  color: "#744210",
                }}
              >
                Stellar
              </div>

              <div className="og-kpi-value">{summary.stellar}</div>

              <div
                className="og-kpi-sub"
                style={{
                  color: "#744210",
                }}
              >
                ≥ 4.5 score
              </div>
            </div>

            <div className="og-kpi">
              <div
                className="og-kpi-label"
                style={{
                  color: "#276749",
                }}
              >
                Vital
              </div>

              <div className="og-kpi-value">{summary.vital}</div>

              <div
                className="og-kpi-sub"
                style={{
                  color: "#276749",
                }}
              >
                3.5–4.4 score
              </div>
            </div>

            <div className="og-kpi">
              <div
                className="og-kpi-label"
                style={{
                  color: "#2c5282",
                }}
              >
                Adequate
              </div>

              <div className="og-kpi-value">{summary.adequate}</div>

              <div
                className="og-kpi-sub"
                style={{
                  color: "#2c5282",
                }}
              >
                2.5–3.4 score
              </div>
            </div>

            <div className="og-kpi">
              <div
                className="og-kpi-label"
                style={{
                  color: "#9b2c2c",
                }}
              >
                Inadequate
              </div>

              <div className="og-kpi-value">{summary.inadequate}</div>

              <div
                className="og-kpi-sub"
                style={{
                  color: "#9b2c2c",
                }}
              >
                Below 2.5 score
              </div>
            </div>
          </div>

          {/* =================================================
              MAIN TABLE CARD
          ================================================== */}

          <div className="">
            {/* =================================================
                FILTERS
            ================================================== */}

            <div
              style={{
                display: "flex",
                gap: 10,
                marginBottom: 14,
              }}
            >
              <input
                className="og-input"
                type="text"
                placeholder="Search employee..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  flex: 1,
                }}
              />

              {/* <select
                className="og-select"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                style={{
                  width: 160,
                }}
              >
                <option value="">All Departments</option>

                {departments.map((item) => (
                  <option key={item?.Id} value={item?.Id}>
                    {item?.Name}
                  </option>
                ))}
              </select> */}

              {/* <select
                className="og-select"
                value={labelFilter}
                onChange={(e) => setLabelFilter(e.target.value)}
                style={{
                  width: 160,
                }}
              >
                <option value="">All Labels</option>

                <option value="Stellar">Stellar</option>

                <option value="Vital">Vital</option>

                <option value="Adequate">Adequate</option>

                <option value="Inadequate">Inadequate</option>

                <option value="Not Assigned">Not Assigned</option>
              </select> */}
            </div>

            {/* =================================================
                ERROR
            ================================================== */}

            {error && (
              <div
                style={{
                  padding: "10px 12px",
                  marginBottom: 12,
                  color: "#9b2c2c",
                  background: "#fff5f5",
                  borderRadius: 6,
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            )}

            {/* =================================================
                TABLE
            ================================================== */}

            <div className="og-table-wrap table-responsive">
              <table className="og-table">
                <thead>
                  <tr>
                    <th>Employee</th>

                    <th>Dept</th>

                    <th
                      style={{
                        width: 80,
                      }}
                    >
                      Computed Score
                    </th>

                    <th
                      style={{
                        width: 90,
                      }}
                    >
                      Manager Score
                    </th>

                    <th>Final Rating Label</th>

                    <th
                      style={{
                        width: 80,
                      }}
                    >
                      Promotion
                    </th>

                    <th
                      style={{
                        width: 64,
                      }}
                    >
                      PIP
                    </th>

                    <th>HR Comment</th>

                    <th
                      style={{
                        width: 80,
                      }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {/* =================================================
                      LOADING
                  ================================================== */}

                  {loading ? (
                    <tr>
                      <td
                        colSpan="9"
                        style={{
                          textAlign: "center",
                          padding: 30,
                        }}
                      >
                        Loading normalization data...
                      </td>
                    </tr>
                  ) : employees.length === 0 ? (
                    /* =================================================
                       EMPTY
                    ================================================== */

                    <tr>
                      <td
                        colSpan="9"
                        style={{
                          textAlign: "center",
                          padding: 30,
                          color: "#8492a6",
                        }}
                      >
                        No employees found.
                      </td>
                    </tr>
                  ) : (
                    /* =================================================
                       DYNAMIC EMPLOYEES
                    ================================================== */

                    employees.map((employee) => {
                      const employeeName = employee?.EmployeeName || "-";

                      const employeeId = employee?.EmpId;

                      const appraisalId = employee?.AppraisalId;

                      const departmentName = employee?.Department || "-";

                      const designation = employee?.Designation || "-";

                      const computedScore = employee?.ComputedScore ?? 0;

                      const managerScore = employee?.ManagerFinalScore ?? 0;

                      // const ratingLabel = employee?.FinalRatingLabelName || "";

                      const ratingLabelId = employee?.FinalRatingLabelId;

                      const profileImage = employee?.ProfileImage;

                      const promotion = Boolean(
                        employee?.IsPromotionRecommended,
                      );

                      const pip = Boolean(employee?.IsPIPRecommended);

                      const hrComment = employee?.HrComment || "";

                      const letterGenerated = Boolean(
                        employee?.LetterGenerated,
                      );

                      return (
                        <tr key={appraisalId || employeeId}>
                          {/* =================================================
                              EMPLOYEE
                          ================================================== */}

                          <td>
                            <div className="og-emp-cell">
                              {profileImage ? (
                                <div
                                  className="og-emp-av"
                                  style={{
                                    background: getAvatarColor(computedScore),
                                    overflow: "hidden",
                                    padding: 0,
                                  }}
                                >
                                  <img
                                    src={profileImage}
                                    alt={employeeName}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                </div>
                              ) : (
                                <div
                                  className="og-emp-av"
                                  style={{
                                    background: getAvatarColor(computedScore),
                                  }}
                                >
                                  {getInitials(employeeName)}
                                </div>
                              )}

                              <div>
                                <div
                                  style={{
                                    fontWeight: 600,
                                  }}
                                >
                                  {employeeName}
                                </div>

                                <div
                                  style={{
                                    fontSize: 11,
                                    color: "#8492a6",
                                  }}
                                >
                                  {designation}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* =================================================
                              DEPARTMENT
                          ================================================== */}

                          <td>{departmentName}</td>

                          {/* =================================================
                              COMPUTED SCORE
                          ================================================== */}

                          <td>
                            <strong style={getScoreStyle(computedScore)}>
                              {computedScore}
                            </strong>
                          </td>

                          {/* =================================================
                              MANAGER SCORE
                          ================================================== */}

                          <td>
                            <strong style={getScoreStyle(managerScore)}>
                              {managerScore}
                            </strong>
                          </td>

                          {/* =================================================
                              FINAL RATING LABEL
                          ================================================== */}

                          <td>
                            <td>
                              <div className="og-norm-label-select">
                                {ratingLabel?.length > 0 ? (
                                  ratingLabel.map((label) => {
                                    const labelId = label?.Id;
                                    const labelName = label?.Name;

                                    const isSelected =
                                      String(ratingLabelId) === String(labelId);

                                    return (
                                      <button
                                        key={labelId}
                                        type="button"
                                        className={`og-norm-label-btn ${getRatingClass(
                                          labelName,
                                        )} ${isSelected ? "selected" : ""}`}
                                        onClick={() =>
                                          handleRatingChange(
                                            appraisalId,
                                            labelId,
                                            labelName,
                                          )
                                        }
                                      >
                                        {labelName}
                                      </button>
                                    );
                                  })
                                ) : (
                                  <span
                                    style={{
                                      fontSize: 12,
                                      color: "#8492a6",
                                    }}
                                  >
                                    No rating labels
                                  </span>
                                )}
                              </div>
                            </td>
                          </td>

                          {/* =================================================
                              PROMOTION
                          ================================================== */}

                          <td
                            style={{
                              textAlign: "center",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={promotion}
                              onChange={(e) =>
                                handlePromotionChange(
                                  appraisalId,
                                  e.target.checked,
                                )
                              }
                              title="Promotion recommended"
                            />
                          </td>

                          {/* =================================================
                              PIP
                          ================================================== */}

                          <td
                            style={{
                              textAlign: "center",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={pip}
                              onChange={(e) =>
                                handlePIPChange(appraisalId, e.target.checked)
                              }
                              title="PIP recommended"
                            />
                          </td>

                          {/* =================================================
                              HR COMMENT
                          ================================================== */}

                          <td>
                            <input
                              className="og-input"
                              type="text"
                              placeholder="Add HR comment..."
                              value={employee?.HrComment || ""}
                              onChange={(e) =>
                                handleHRCommentChange(
                                  employee.AppraisalId,
                                  e.target.value,
                                )
                              }
                              onBlur={() =>
                                handleHRCommentBlur(employee.AppraisalId)
                              }
                            />
                          </td>

                          {/* =================================================
                              LETTER
                          ================================================== */}

                          <td>
                            <button
                              type="button"
                              className="btn-og btn-og-dark btn-sm"
                              onClick={() =>
                                viewAppraisalDetails(employee?.AppraisalGuid)
                              }
                            >
                              <i className="fa fa-eye"></i> View
                            </button>
                            {employee?.CurrentStageId != 5 && (
                              <button
                                type="button"
                                className="btn-og btn-og-secondary btn-og-sm"
                                onClick={() =>
                                  publishEmployee(employee.AppraisalId)
                                }
                              >
                                <i className="fa fa-file-lines"></i> Publish
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* =================================================
                INFORMATION
            ================================================== */}

            <div
              style={{
                fontSize: 12,
                color: "#8492a6",
                marginTop: 10,
              }}
            >
              <i className="fa fa-circle-info"></i> Computed Score is the
              system-calculated weighted average. Final Rating Label is set
              manually. PIP flag is recommended automatically for Inadequate but
              can be overridden.
            </div>
          </div>
        </div>
      </div>

      <div
        className={`og-modal-overlay ${closeCyclePopup ? "open" : ""}`}
        onClick={() => setCloseCyclePopup(false)}
      >
        <div className="og-modal" onClick={(event) => event.stopPropagation()}>
          <div className="og-modal-header">
            <div className="og-modal-title">
              <i
                className="fa fa-flag-checkered"
                style={{ color: "#276749" }}
              ></i>{" "}
              Mark Cycle as Complete
            </div>
            <button
              type="button"
              className="og-modal-close"
              onClick={() => setCloseCyclePopup(false)}
            >
              <i className="fa fa-times"></i>
            </button>
          </div>
          <div className="modal-body">
            <div
              style={{
                background: "#fffbeb",
                border: "1px solid #fbd38d",
                borderRadius: 8,
                padding: 12,
                marginBottom: 14,
                fontSize: 13,
                color: "#744210",
              }}
            >
              <i
                className="fa fa-triangle-exclamation"
                style={{ color: "#d97706" }}
              ></i>{" "}
              <strong>
                {normalizeAppraisal?.PendingCount || 0} employees still have no
                rating label assigned.
              </strong>{" "}
              Completing now will lock the cycle. Unfinished employees will have
              no final label recorded.
            </div>
            <div style={{ fontSize: 13, color: "#8492a6", lineHeight: 1.7 }}>
              Marking complete will:
              <br />· Close all pending self-assessments and reviews
              <br />· Lock the cycle — no further edits possible
              <br />· Make appraisal letters available to generate
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn-og btn-og-secondary"
              onClick={() => setCloseCyclePopup(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-og btn-og-primary"
              onClick={() => setCloseCyclePopup(false)}
              style={{ background: "#276749" }}
            >
              <i className="fa fa-scale-balanced"></i> Finish Normalizing First
            </button>
            <button
              type="button"
              className="btn-og btn-og-dark"
              onClick={confirmMarkAsComplete}
            >
              <i className="fa fa-flag-checkered"></i> Complete Anyway
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <Suspense>
          <ComponentLoader />
        </Suspense>
      ) : (
        ""
      )}
    </div>
  );
};

export default AssessmentNormalization;
